import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SereneHeart } from "@/lib/brand";

const OFFLINE = "The connection was interrupted. Please try again — your invitation is still yours.";
const FRIENDLY = {
  "INVALID INVITATION": "We don't recognise this invitation. Please check the code.",
  "INVITATION ALREADY USED": "This invitation has already been accepted.",
  "INVITATION EXPIRED": "This invitation has expired.",
  "INVITATION REVOKED": "This invitation is no longer valid.",
  "INVITATION NOT YET ACTIVE": "This invitation isn't active yet.",
  "THIS INVITATION IS BEING USED BY SOMEONE ELSE": "This invitation is currently being accepted by someone else.",
};
const FONT = { fontFamily: '"Cormorant Garamond", Georgia, serif' };
const LABEL = "block text-[10px] tracking-[0.32em] uppercase text-[#9C907A] mb-2";
const LINE = "w-full bg-transparent border-0 border-b border-[#C9BCA2] rounded-none px-0 py-2 text-lg text-[#2E2A22] placeholder:text-[#B8AC95] focus:outline-none focus:border-[#2E2A22] transition-colors";

const WelcomeFade = ({ firstName, url }) => {
  useEffect(() => {
    const t = setTimeout(() => { if (url) window.location.href = url; }, 2000);
    return () => clearTimeout(t);
  }, [url]);
  return (
    <div className="text-center space-y-6 srn-welcome-fade" data-testid="welcome-fade">
      <SereneHeart size={44} className="mx-auto" />
      <h1 className="font-serif text-3xl tracking-[0.2em] text-[#2E2A22]" data-testid="welcome-heading">Welcome, {firstName}</h1>
      <p className="text-[11px] tracking-[0.3em] text-[#9C907A]">TAKING YOU TO THE MAISON</p>
    </div>
  );
};

const Field = ({ label, testid, ...props }) => (
  <label className="block">
    <span className={LABEL}>{label}</span>
    <input data-testid={testid} className={LINE} style={FONT} {...props} />
  </label>
);

export default function Join() {
  const navigate = useNavigate();
  const { code: paramCode } = useParams();
  const [form, setForm] = useState({ code: (paramCode || "").toUpperCase(), first_name: "", last_name: "", email: "" });
  const [policies, setPolicies] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: k === "code" ? e.target.value.toUpperCase() : e.target.value });
  const complete = form.code.trim() && form.first_name.trim() && form.last_name.trim() && form.email.trim();

  useEffect(() => {
    api.get("/public-config").then((r) => setPolicies(r.data.policies || {})).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, code: form.code.trim().toUpperCase(), consent: true }, { timeout: 70000 });
      setDone(data);
    } catch (err) {
      const msg = err.response ? formatApiErrorDetail(err.response.data?.detail) : OFFLINE;
      setError(FRIENDLY[msg] || msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-16" style={{ backgroundColor: "#F3EEE4", ...FONT }} data-testid="join-screen">
      {done ? <WelcomeFade firstName={done.first_name || form.first_name} url={done.store_login_url} /> : (
        <form onSubmit={submit} className="w-full max-w-md" data-testid="join-form">
          <div className="text-center mb-12">
            <div className="mb-6"><SereneHeart size={40} className="mx-auto" /></div>
            <h1 className="font-serif text-3xl tracking-[0.2em] text-[#2E2A22]" data-testid="join-title">Your Invitation</h1>
            <p className="mt-3 text-base italic text-[#9C907A]" data-testid="join-subtitle">The Maison awaits.</p>
          </div>
          <div className="space-y-9">
            {paramCode
              ? <p className="text-center text-xs tracking-[0.32em] text-[#6B6151]" data-testid="invitation-code-display">INVITATION · {form.code}</p>
              : <Field label="Invitation code" testid="invitation-code-input" name="code" required placeholder="SRN-XXXX-XXXX" value={form.code} onChange={set("code")} />}
            <div className="grid grid-cols-2 gap-6">
              <Field label="First name" testid="first-name-input" name="first_name" required value={form.first_name} onChange={set("first_name")} />
              <Field label="Last name" testid="last-name-input" name="last_name" required value={form.last_name} onChange={set("last_name")} />
            </div>
            <div>
              <Field label="Email" testid="email-input" name="email" type="email" required value={form.email} onChange={set("email")} />
              <p className="mt-2 text-sm italic text-[#9C907A]" data-testid="email-note">Sign in to the Maison with this exact email.</p>
            </div>
          </div>
          {error && <p data-testid="register-error" className="mt-8 text-base text-[#8E7468]">{error}</p>}
          <button data-testid="create-membership-btn" type="submit" disabled={loading || !complete} style={FONT}
            className={`mt-10 w-full h-12 text-xs tracking-[0.32em] bg-[#2E2A22] text-[#F3EEE4] transition-opacity hover:bg-[#201C15] ${complete && !loading ? "opacity-100" : "opacity-70 cursor-default"}`}>
            {loading ? "ACCEPTING…" : "ACCEPT INVITATION"}
          </button>
          <p className="mt-4 text-center text-sm text-[#9C907A]" data-testid="consent-note">
            By accepting, you agree to the <a data-testid="terms-link" href={policies.terms || "#"} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-[#2E2A22]">Terms</a> and <a data-testid="privacy-link" href={policies.privacy || "#"} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-[#2E2A22]">Privacy Policy</a>
          </p>
          <button type="button" data-testid="join-back-btn" onClick={() => navigate("/")} className="block mx-auto mt-8 text-xs tracking-[0.3em] text-[#9C907A] hover:text-[#6B6151]">← BACK</button>
        </form>
      )}
    </div>
  );
}
